import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Spinner,
  Textarea,
  Tooltip,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Send24Regular,
  Add24Regular,
  ChatMultiple24Regular,
  Delete16Regular,
  DocumentText16Regular,
} from '@fluentui/react-icons';
import {
  type Citation,
  type ChatMessage,
  type Conversation,
  createConversation,
  deleteConversation as apiDeleteConversation,
  getConversation,
  listConversations,
  sendMessageStream,
} from '../api/chat.js';
import { refreshUsageSoon } from '../stores/usageStore.js';
import { useConfirm } from './ConfirmDialog.js';
import { useCanSendChat } from '../stores/quotaGuards.js';
import type { ProjectIndexStatus } from '../api/projects.js';

interface Props {
  projectId: string;
  /** Lista de archivos para resolver citas por nombre cuando el modelo no manda fileId. */
  files: { id: string; originalName: string }[];
  /** Estado del indexado RAG — para avisar (no bloquear) si aún se procesa. */
  indexStatus?: ProjectIndexStatus;
  chunksTotal?: number;
  chunksEmbedded?: number;
  /** True si algún archivo sigue en lectura (parse). */
  parsingInFlight?: boolean;
}

interface UIMessage extends ChatMessage {
  /** True mientras está streamando tokens. */
  streaming?: boolean;
}

// =====================================================================

const useStyles = makeStyles({
  root: {
    flex: 1,
    minHeight: 0,
    display: 'grid',
    gridTemplateColumns: '180px 1fr',
    gap: 'var(--space-m)',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-xl)',
    overflow: 'hidden',
  },
  sidebar: {
    background: 'var(--bg-surface-2, rgba(255,255,255,0.02))',
    borderRight: '1px solid var(--border-subtle)',
    padding: 'var(--space-s)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-xs)',
    overflow: 'auto',
  },
  sideHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    padding: '4px 6px',
  },
  convItem: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-l, 12px)',
    padding: '8px 10px',
    textAlign: 'left',
    cursor: 'pointer',
    color: 'var(--text-primary)',
    fontSize: 'var(--fs-200)',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    transition: 'background 0.12s ease',
    ':hover': { background: 'var(--bg-raised)' },
    // Borrar: oculto por defecto, aparece al pasar el mouse o al enfocar.
    '&:hover button': { opacity: 1 },
    '&:focus-within button': { opacity: 1 },
  },
  convItemActive: {
    background: 'var(--accent-weak)',
    ':hover': { background: 'var(--accent-weak)' },
  },
  convItemTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  convTitle: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 500,
  },
  convDelete: {
    opacity: 0,
    flexShrink: 0,
    minWidth: '24px',
    height: '24px',
    padding: 0,
    transition: 'opacity 0.12s ease',
  },
  convTip: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    maxWidth: '260px',
  },
  convTipTitle: {
    fontSize: 'var(--fs-200)',
    fontWeight: 600,
    color: 'var(--text-primary)',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.4,
  },
  convTipWhen: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-100)',
    color: 'var(--text-secondary)',
  },
  main: { display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 },
  indexBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-s)',
    padding: 'var(--space-s) var(--space-l)',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'rgba(245,158,11,0.10)',
    color: 'var(--text-secondary)',
    fontSize: 'var(--fs-200)',
    lineHeight: 1.4,
  },
  indexBannerStrong: { color: 'var(--text-primary)', fontWeight: 600 },
  messages: {
    flex: 1,
    overflow: 'auto',
    padding: 'var(--space-xl) var(--space-xxl)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-xl)',
  },
  // Texto compartido de los mensajes. El asistente NO lleva burbuja (texto
  // abierto, como las apps de IA modernas); solo el usuario tiene burbuja.
  bubble: {
    fontSize: 'var(--fs-300)',
    lineHeight: 1.65,
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  user: {
    alignSelf: 'flex-end',
    maxWidth: '78%',
    background: 'var(--accent-weak)',
    borderRadius: '16px',
    padding: '9px 14px',
    color: 'var(--text-primary)',
  },
  assistant: {
    alignSelf: 'stretch',
    maxWidth: '46rem',
    color: 'var(--text-primary)',
  },
  tool: {
    alignSelf: 'flex-start',
    background: 'rgba(180,200,255,0.04)',
    border: '1px dashed var(--border-subtle)',
    borderRadius: 'var(--radius-m)',
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-100)',
    color: 'var(--text-secondary)',
    padding: 'var(--space-s) var(--space-m)',
  },
  citationRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: 'var(--space-s)',
  },
  citePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    background: 'rgba(120,140,255,0.10)',
    border: '1px solid rgba(120,140,255,0.30)',
    color: 'var(--text-primary)',
    borderRadius: 'var(--radius-pill, 999px)',
    padding: '2px 8px',
    fontSize: 'var(--fs-100)',
    fontFamily: 'var(--font-mono)',
    cursor: 'pointer',
    textDecoration: 'none',
  },
  composer: {
    borderTop: '1px solid var(--border-subtle)',
    padding: 'var(--space-l) var(--space-xl)',
    display: 'flex',
    gap: 'var(--space-m)',
    alignItems: 'flex-end',
    background: 'rgba(255,255,255,0.015)',
  },
  composerInput: {
    flex: 1,
    minHeight: '88px',
    fontSize: 'var(--fs-300)',
    lineHeight: 1.5,
  },
  empty: {
    flex: 1,
    display: 'grid',
    placeItems: 'center',
    color: 'var(--text-tertiary)',
    textAlign: 'center',
    padding: 'var(--space-4xl) var(--space-xxl)',
  },
  emptyInner: {
    maxWidth: '520px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--space-m)',
  },
  emptyIcon: {
    width: '88px',
    height: '88px',
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background:
      'radial-gradient(circle at 30% 30%, rgba(120,140,255,0.22), rgba(180,140,255,0.08))',
    border: '1px solid rgba(120,140,255,0.30)',
    color: 'var(--text-primary)',
    boxShadow: '0 0 32px rgba(120,140,255,0.18)',
    marginBottom: 'var(--space-s)',
  },
  emptyTitle: {
    margin: 0,
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--fs-700)',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: 'var(--text-primary)',
  },
  emptySub: {
    margin: 0,
    fontSize: 'var(--fs-400)',
    color: 'var(--text-secondary)',
    lineHeight: 1.55,
  },
  suggestions: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 'var(--space-s)',
    marginTop: 'var(--space-m)',
  },
  suggestionChip: {
    appearance: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 'var(--fs-300)',
    color: 'var(--text-secondary)',
    background: 'var(--bg-surface-2, rgba(255,255,255,0.02))',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-pill, 999px)',
    padding: '7px 13px',
    transition: 'background 0.15s, color 0.15s, border-color 0.15s',
    ':hover': {
      background: 'var(--accent-weak)',
      border: '1px solid var(--accent)',
      color: 'var(--accent)',
    },
    ':disabled': { cursor: 'not-allowed', opacity: 0.5 },
  },
});

// Preguntas de arranque (entry points del chat). Set definido por dirección en
// el mockup M4 — preguntas genéricas de licitación, no contextuales (v1).
const SUGGESTED_QUESTIONS = [
  '¿Cuál es el plazo y la fecha de entrega?',
  '¿Qué documentos requiere el concursante?',
  '¿Hay anticipo? ¿De cuánto?',
];

// =====================================================================

export function ChatPanel({
  projectId,
  files,
  indexStatus,
  chunksTotal = 0,
  chunksEmbedded = 0,
  parsingInFlight = false,
}: Props) {
  const styles = useStyles();
  const confirm = useConfirm();

  // Aviso (no bloqueante): la documentación todavía se está procesando, así que
  // la búsqueda semántica solo ve los fragmentos ya indexados → respuestas
  // posiblemente incompletas. El chat sigue habilitado (read_full_file ya sirve).
  const indexing =
    parsingInFlight || indexStatus === 'chunking' || indexStatus === 'embedding';
  // Proyecto sin texto para buscar: ya no indexa y no quedaron fragmentos (solo
  // planos u otros archivos sin texto). No hay de qué preguntarle → se avisa y se
  // deshabilita el envío, en vez de dejar preguntar contra el vacío.
  const sinTexto = chunksTotal === 0 && !indexing;
  const indexPct =
    chunksTotal > 0
      ? Math.min(100, Math.max(0, Math.round((chunksEmbedded / chunksTotal) * 100)))
      : null;
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingConv, setLoadingConv] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Conversación recién creada localmente (primer mensaje): su estado se está
  // streameando en vivo. El efecto de carga NO debe refetchearla — un GET aquí
  // devuelve [] (aún no persiste) y pisaría las burbujas optimistas + los tokens
  // en vuelo, dejando el chat "mudo" hasta recargar (bug AI-2).
  const skipLoadConvRef = useRef<string | null>(null);
  const sendGuard = useCanSendChat();
  const canMutate = sendGuard.allowed;

  // Mapa para resolver fileId -> nombre real cuando hace falta abrir el archivo.
  const filesById = useMemo(() => new Map(files.map((f) => [f.id, f.originalName])), [files]);

  // ---- carga inicial: lista de conversaciones ----
  // Con red intermitente el refresh fallaba en silencio y la lista quedaba
  // vieja ("no guardó en la lista", QA 2026-07-03) → reintento con backoff.
  const refreshConversations = useCallback(async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const list = await listConversations(projectId);
        setConversations(list);
        return list;
      } catch (err) {
        if (attempt === 2) setError((err as Error).message);
        else await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
      }
    }
    return [];
  }, [projectId]);

  useEffect(() => {
    void refreshConversations();
  }, [refreshConversations]);

  // ---- cargar mensajes al cambiar de conversación ----
  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }
    // Recién creada + streameando: ya tenemos su estado local, no refetchear
    // (evita que un GET vacío pise las burbujas y los tokens en vuelo — AI-2).
    if (skipLoadConvRef.current === activeId) {
      skipLoadConvRef.current = null;
      return;
    }
    setLoadingConv(true);
    getConversation(projectId, activeId)
      .then((data) => {
        setMessages(data.messages);
        setLoadingConv(false);
      })
      .catch((err) => {
        setError((err as Error).message);
        setLoadingConv(false);
      });
  }, [activeId, projectId]);

  // auto-scroll al fondo cuando hay nuevos mensajes
  useEffect(() => {
    messagesRef.current?.scrollTo({ top: messagesRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  async function onNew() {
    try {
      const c = await createConversation(projectId);
      setConversations((prev) => [c, ...prev]);
      setActiveId(c.id);
      setMessages([]);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function onDelete(id: string) {
    if (!(await confirm({
      title: 'Eliminar conversación',
      message: '¿Eliminar esta conversación?',
      confirmLabel: 'Eliminar',
      destructive: true,
    }))) return;
    await apiDeleteConversation(projectId, id);
    setConversations((prev) => prev.filter((c) => c.id !== id));
    if (activeId === id) setActiveId(null);
  }

  async function ensureConversation(): Promise<string | null> {
    if (activeId) return activeId;
    try {
      const c = await createConversation(projectId);
      setConversations((prev) => [c, ...prev]);
      // Marcar ANTES de setActiveId: el efecto de carga que dispara este cambio
      // debe saltarse el fetch (la conversación es nuestra y se está streameando).
      skipLoadConvRef.current = c.id;
      setActiveId(c.id);
      return c.id;
    } catch (err) {
      setError((err as Error).message);
      return null;
    }
  }

  async function onSend(override?: string) {
    const content = (override ?? draft).trim();
    if (!content || sending) return;
    const convId = await ensureConversation();
    if (!convId) return;

    setDraft('');
    setSending(true);
    setError(null);

    const tempUserId = `tmp-user-${Date.now()}`;
    const tempAsstId = `tmp-asst-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: tempUserId,
        conversationId: convId,
        role: 'user',
        content,
        citations: [],
        toolCalls: [],
        tokenUsage: null,
        model: null,
        toolName: null,
        createdAt: new Date().toISOString(),
      },
      {
        id: tempAsstId,
        conversationId: convId,
        role: 'assistant',
        content: '',
        citations: [],
        toolCalls: [],
        tokenUsage: null,
        model: null,
        toolName: null,
        createdAt: new Date().toISOString(),
        streaming: true,
      },
    ]);

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      await sendMessageStream({
        projectId,
        conversationId: convId,
        content,
        signal: ctrl.signal,
        onEvent: (ev) => {
          if (ev.type === 'token') {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === tempAsstId ? { ...m, content: m.content + ev.delta } : m,
              ),
            );
          } else if (ev.type === 'tool_call_start') {
            setMessages((prev) => [
              ...prev,
              {
                id: `tool-${ev.id}`,
                conversationId: convId,
                role: 'tool',
                content: `▸ ${toolLabel(ev.name)}…`,
                citations: [],
                toolCalls: [],
                tokenUsage: null,
                model: null,
                toolName: ev.name,
                createdAt: new Date().toISOString(),
              },
            ]);
          } else if (ev.type === 'tool_call_complete') {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === `tool-${ev.id}`
                  ? {
                      ...m,
                      content: `▸ ${toolLabel(ev.name)} ✓`,
                    }
                  : m,
              ),
            );
          } else if (ev.type === 'assistant_message_persisted') {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === tempAsstId
                  ? {
                      ...m,
                      id: ev.messageId,
                      citations: ev.citations,
                      model: ev.model,
                      streaming: false,
                    }
                  : m,
              ),
            );
          } else if (ev.type === 'usage') {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === tempAsstId || m.role === 'assistant'
                  ? { ...m, tokenUsage: { input: ev.input, output: ev.output } }
                  : m,
              ),
            );
          } else if (ev.type === 'error') {
            setError(ev.error);
            // La burbuja del asistente no se queda vacía/muda: si no alcanzó a
            // generar texto, mostramos un aviso amable (reunión 2026-07-02).
            setMessages((prev) => prev.map((m) => (m.id === tempAsstId
              ? {
                  ...m,
                  streaming: false,
                  content: m.content?.trim()
                    ? m.content
                    : 'No pude generar una respuesta esta vez. Vuelve a intentarlo en unos momentos.',
                }
              : m)));
          }
        },
      });
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setError((err as Error).message);
      }
    } finally {
      setSending(false);
      abortRef.current = null;
      // refrescar listado para reflejar título auto-generado
      void refreshConversations();
      // refrescar meter de chats (el server ya incrementó al inicio del turn)
      refreshUsageSoon();
    }
  }

  function onCancel() {
    abortRef.current?.abort();
    abortRef.current = null;
    setSending(false);
  }

  function onCitationClick(c: Citation) {
    // Sin viewer aún: abrimos el download directo del API (download endpoint).
    if (!c.fileId) return;
    const page = c.pageNum ? `#page=${c.pageNum}` : '';
    window.open(`/api/v1/projects/${projectId}/files/${c.fileId}/download${page}`, '_blank');
  }

  return (
    <div className={styles.root}>
      {/* sidebar conversaciones */}
      <div className={styles.sidebar}>
        <div className={styles.sideHeader}>
          <span>chats</span>
          <Button
            size="small"
            appearance="subtle"
            icon={<Add24Regular />}
            onClick={() => void onNew()}
            title="Nuevo chat"
          />
        </div>

        {conversations.length === 0 && (
          <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--fs-100)', padding: 8 }}>
            (sin conversaciones)
          </div>
        )}

        {conversations.map((c) => {
          const title = c.title || 'Sin título';
          const when = c.lastMessageAt
            ? new Date(c.lastMessageAt).toLocaleString('es-MX', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })
            : null;
          return (
            <Tooltip
              key={c.id}
              relationship="description"
              positioning="after"
              content={
                <div className={styles.convTip}>
                  <span className={styles.convTipTitle}>{title}</span>
                  {when && <span className={styles.convTipWhen}>{when}</span>}
                </div>
              }
            >
              <div
                className={mergeClasses(styles.convItem, c.id === activeId && styles.convItemActive)}
                onClick={() => setActiveId(c.id)}
              >
                <div className={styles.convItemTop}>
                  <span className={styles.convTitle}>{title}</span>
                  <Button
                    appearance="transparent"
                    size="small"
                    icon={<Delete16Regular />}
                    className={styles.convDelete}
                    aria-label="Eliminar conversación"
                    onClick={(e) => {
                      e.stopPropagation();
                      void onDelete(c.id);
                    }}
                  />
                </div>
              </div>
            </Tooltip>
          );
        })}
      </div>

      {/* main */}
      <div className={styles.main}>
        {indexing && (
          <div className={styles.indexBanner}>
            <Spinner size="extra-tiny" />
            <span>
              <span className={styles.indexBannerStrong}>Procesando la documentación…</span>{' '}
              {indexPct != null ? `${indexPct}% indexado. ` : ''}
              Las respuestas pueden estar incompletas hasta terminar.
            </span>
          </div>
        )}
        {!activeId && messages.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyInner}>
              <div className={styles.emptyIcon}>
                <ChatMultiple24Regular fontSize={40} />
              </div>
              <h3 className={styles.emptyTitle}>Pregúntale al proyecto</h3>
              <p className={styles.emptySub}>
                Escribe abajo tu pregunta y la inteligencia artificial responderá con citaciones directas a los
                archivos del proyecto.
              </p>
              <div className={styles.suggestions}>
                {SUGGESTED_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    type="button"
                    className={styles.suggestionChip}
                    disabled={sending || !canMutate || sinTexto}
                    onClick={() => void onSend(q)}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div ref={messagesRef} className={styles.messages}>
            {loadingConv && <Spinner size="tiny" />}
            {messages.map((m) => (
              <MessageBubble
                key={m.id}
                m={m}
                styles={styles}
                filesById={filesById}
                onCitationClick={onCitationClick}
              />
            ))}
          </div>
        )}

        {error && (
          <div
            style={{
              padding: '8px 12px',
              color: 'var(--semantic-danger)',
              fontSize: 'var(--fs-200)',
              borderTop: '1px solid var(--border-subtle)',
            }}
          >
            {error}
          </div>
        )}

        <div className={styles.composer}>
          <Textarea
            className={styles.composerInput}
            value={draft}
            onChange={(_, d) => setDraft(d.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canMutate) {
                e.preventDefault();
                void onSend();
              }
            }}
            placeholder={
              !canMutate
                ? sendGuard.message ?? 'Chat deshabilitado.'
                : sinTexto
                  ? 'Este proyecto solo tiene planos, sin texto. Sube el pliego para poder preguntar.'
                  : 'Pregunta sobre tu proyecto…  (⌘/Ctrl+Enter para enviar)'
            }
            resize="vertical"
            rows={4}
            disabled={sending || !canMutate || sinTexto}
          />
          {sending ? (
            <Tooltip content="Cancelar generación" relationship="label">
              <Button appearance="secondary" onClick={onCancel}>
                Cancelar
              </Button>
            </Tooltip>
          ) : (
            <Tooltip
              content={canMutate ? '' : sendGuard.message ?? ''}
              relationship="label"
              visible={canMutate ? false : undefined}
            >
              <Button
                appearance="primary"
                icon={<Send24Regular />}
                disabled={!draft.trim() || !canMutate || sinTexto}
                onClick={() => void onSend()}
              >
                Enviar
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================================

interface BubbleProps {
  m: UIMessage;
  styles: ReturnType<typeof useStyles>;
  filesById: Map<string, string>;
  onCitationClick: (c: Citation) => void;
}

function MessageBubble({ m, styles, filesById, onCitationClick }: BubbleProps) {
  if (m.role === 'tool') {
    // Derivar la etiqueta amigable del toolName (fuente de verdad) — cubre tanto
    // el mensaje en vivo como el recargado de la BD. Nunca mostramos file_id/args.
    const done = (m.content ?? '').includes('✓');
    const text = m.toolName
      ? `▸ ${toolLabel(m.toolName)}${done ? ' ✓' : '…'}`
      : (m.content ?? '');
    return <div className={mergeClasses(styles.bubble, styles.tool)}>{text}</div>;
  }
  const cls =
    m.role === 'user'
      ? mergeClasses(styles.bubble, styles.user)
      : mergeClasses(styles.bubble, styles.assistant);
  return (
    <div className={cls}>
      {/* Render del contenido reemplazando [[file:...]] por chips inline */}
      <RenderedContent text={m.content} citations={m.citations} onCitationClick={onCitationClick} />
      {m.streaming && (
        <span style={{ opacity: 0.5, marginLeft: 4 }}>▍</span>
      )}
      {m.role === 'assistant' && m.citations.length > 0 && (
        <div className={styles.citationRow}>
          {m.citations.map((c, i) => (
            <span
              key={`${c.fileId}-${c.pageNum ?? '_'}-${i}`}
              className={styles.citePill}
              onClick={() => onCitationClick(c)}
              title={c.snippet ?? c.fileName}
            >
              <DocumentText16Regular fontSize={12} />
              {abbreviate(c.fileName)}
              {c.pageNum ? ` · p.${c.pageNum}` : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// =====================================================================

interface RenderedContentProps {
  text: string;
  citations: Citation[];
  onCitationClick: (c: Citation) => void;
}

/**
 * Reemplaza inline las marcas [[file:NOMBRE#p=N]] por un chip clicable
 * (sin librería markdown — es solo un parser regex).
 */
function RenderedContent({ text, citations, onCitationClick }: RenderedContentProps) {
  const re = /\[\[file:([^\]\n]+?)(?:#p=(\d+))?\]\]/g;
  const out: React.ReactNode[] = [];
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) out.push(text.slice(lastIdx, m.index));
    const name = (m[1] ?? '').trim();
    const page = m[2] ? Number(m[2]) : undefined;
    const cite = citations.find(
      (c) => c.fileName.toLowerCase() === name.toLowerCase() && (page == null || c.pageNum === page),
    ) ?? { fileId: '', fileName: name, pageNum: page ?? null };
    out.push(
      <span
        key={`cite-${key++}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          background: 'rgba(120,140,255,0.16)',
          padding: '0 6px',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: '0.92em',
        }}
        onClick={() => onCitationClick(cite)}
      >
        <DocumentText16Regular fontSize={12} />
        {abbreviate(name)}
        {page ? `·p.${page}` : ''}
      </span>,
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) out.push(text.slice(lastIdx));
  return <>{out}</>;
}

function abbreviate(name: string): string {
  if (name.length <= 36) return name;
  const idx = name.lastIndexOf('/');
  return idx >= 0 ? `…/${name.slice(idx + 1)}` : `${name.slice(0, 32)}…`;
}

// Etiqueta humana para el chip de herramienta. NUNCA exponer al cliente la
// mecánica interna (nombre de tool, file_id, max_chars, etc.): solo la ACCIÓN
// en lenguaje de negocio.
const TOOL_LABELS: Record<string, string> = {
  read_full_file: 'Leyendo el documento',
  search_project: 'Buscando en los documentos del proyecto',
  search_ecostos: 'Consultando el catálogo de referencia',
  search_custom_db: 'Consultando tu base de datos',
  // El chat ya NO busca en internet (se retiró el 2026-08-18). La etiqueta se
  // queda porque las conversaciones guardadas de cuando sí existía traen este
  // nombre, y sin ella se leerían como "Consultando información".
  web_search: 'Buscando en la web',
};
function toolLabel(name: string | null): string {
  return (name && TOOL_LABELS[name]) || 'Consultando información';
}
